/**
 * src/services/adminApi.ts
 *
 * File-level:
 * - Backwards-compatible admin API helper used by admin UI components.
 * - Exposes `loginAdmin` (used by existing components) and `pingAdminLogin`.
 * - Uses centralized API configuration for consistent URL management.
 */
import { getApiBaseUrl } from '@/config/api';

/**
 * AdminUser
 *
 * @description Basic shape of an authenticated admin user returned by the backend.
 */
export interface AdminUser {
  userId: string;
  displayName: string;
}

/**
 * AdminLoginPayload
 *
 * @description Request payload for admin login.
 */
export interface AdminLoginPayload {
  userId: string;
  password: string;
}

/**
 * AdminLoginResponse
 *
 * @description Normalized response shape returned by loginAdmin.
 */
export interface AdminLoginResponse {
  success: boolean;
  user?: AdminUser | null;
  message?: string;
  error?: string | null;
}

/**
 * getApiBase
 *
 * @description Return configured backend origin using centralized configuration.
 * @returns string
 */
function getApiBase(): string {
  return getApiBaseUrl();
}

/**
 * loginAdmin
 *
 * @description Call backend POST /api/admin/auth/login with { userId, password }.
 *              Returns a normalized AdminLoginResponse.
 *
 * @param payload AdminLoginPayload
 * @returns Promise<AdminLoginResponse>
 */
export async function loginAdmin(payload: AdminLoginPayload): Promise<AdminLoginResponse> {
  const base = getApiBase();
  try {
    const res = await fetch(`${base}/api/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: payload.userId, password: payload.password }),
      credentials: 'same-origin',
    });

    // Try to parse JSON safely
    const json = await res.json().catch(() => null);

    if (!res.ok) {
      return {
        success: false,
        message: (json && json.message) || `Login failed (${res.status})`,
        error: (json && json.error) || null,
      };
    }

    // Expecting { success: true, user: { userId, displayName } }
    if (!json || !json.success) {
      return {
        success: false,
        message: (json && json.message) || 'Invalid server response',
        error: (json && json.error) || null,
      };
    }

    return {
      success: true,
      user: json.user || null,
      message: json.message || 'Login successful',
    };
  } catch (err: any) {
    return {
      success: false,
      message: 'Network error while contacting server',
      error: String(err?.message || err),
    };
  }
}

/**
 * pingAdminLogin
 *
 * @description Small helper to check availability of the admin-login endpoint (returns boolean).
 * @returns Promise<boolean>
 */
export async function pingAdminLogin(): Promise<boolean> {
  const base = getApiBase();
  try {
    const res = await fetch(`${base}/api/admin/auth/login`, { method: 'OPTIONS' });
    return res.status >= 200 && res.status < 500;
  } catch {
    return false;
  }
}

// Backwards-compatible named exports: keep older names available if other files expect them.
/**
 * loginAdmin2
 *
 * @description Alias for loginAdmin to support different import names across the codebase.
 */
export const loginAdmin2 = loginAdmin;

/**
 * pingAdminLogin2
 *
 * @description Alias for pingAdminLogin to support different import names across the codebase.
 */
export const pingAdminLogin2 = pingAdminLogin;
